import {
  formatRecallDate,
  recallSeverityForCard,
  str,
  strOrUndefined,
} from '../utils';

import type {
  CpscRecallRecord,
  FdaEnforcementRecord,
  RecallItem,
  UsdaRecallRecord,
  VehicleRecallRecord,
} from '../interfaces';
import type { RecallDetailConfig } from './types';

const RECALL_SOURCE = {
  VEHICLE: 'vehicle',
  FDA_DRUG: 'fdaDrug',
  FDA_DEVICE: 'fdaDevice',
  FDA_FOOD: 'fdaFood',
  USDA: 'usda',
  CPSC: 'cpsc',
} as const;

function isUsdaTruthyFlag(v: unknown): boolean {
  return typeof v === 'string' && v.trim().toLowerCase() === 'true';
}

function deriveUsdaRecallStatus(r: UsdaRecallRecord): string | undefined {
  if (isUsdaTruthyFlag(r.field_archive_recall)) {
    return 'Archived';
  }
  if (strOrUndefined(r.field_closed_date)) {
    return 'Closed';
  }
  if (isUsdaTruthyFlag(r.field_active_notice)) {
    return 'Ongoing';
  }
  return undefined;
}

export function getDetailConfig(item: RecallItem): RecallDetailConfig {
  const recallDate = formatRecallDate(item.date);

  switch (item.source) {
    case RECALL_SOURCE.VEHICLE: {
      const r = item.record as VehicleRecallRecord;
      return {
        recallDate,
        status: strOrUndefined(r.status),
        severity: undefined,
        subtitle:
          strOrUndefined(r.oem_description) ?? strOrUndefined(r.components),
        title: strOrUndefined(r.summary) ?? strOrUndefined(r.consequence),
        fields: [
          { label: 'Summary', value: strOrUndefined(r.summary) },
          { label: 'Consequence', value: strOrUndefined(r.consequence) },
          { label: 'Remedy', value: strOrUndefined(r.remedy) },
        ],
      };
    }
    case RECALL_SOURCE.FDA_DRUG: {
      const r = item.record as FdaEnforcementRecord;
      return {
        recallDate,
        status: strOrUndefined(r.status),
        severity: recallSeverityForCard(r.classification),
        subtitle: strOrUndefined(r.recalling_firm),
        title:
          strOrUndefined(r.product_description) ??
          strOrUndefined(r.reason_for_recall),
        fields: [
          {
            label: 'Recall Reason',
            value: strOrUndefined(r.reason_for_recall),
          },
          { label: 'Address/Contact Info', value: undefined },
          {
            label: 'Affected Products',
            value: strOrUndefined(r.product_description),
          },
          {
            label: 'Affected Locations',
            value: strOrUndefined(r.distribution_pattern),
          },
          {
            label: 'Quantity Recalled',
            value: strOrUndefined(r.product_quantity),
          },
          { label: 'Recall Number', value: strOrUndefined(r.recall_number) },
        ],
      };
    }
    case RECALL_SOURCE.FDA_DEVICE: {
      const r = item.record as FdaEnforcementRecord;
      return {
        recallDate,
        status: strOrUndefined(r.status),
        severity: recallSeverityForCard(r.classification),
        subtitle: strOrUndefined(r.recalling_firm),
        title:
          strOrUndefined(r.product_description) ??
          strOrUndefined(r.reason_for_recall),
        fields: [
          {
            label: 'Recall Reason',
            value: strOrUndefined(r.reason_for_recall),
          },
          { label: 'Address/Contact Info', value: undefined },
          {
            label: 'Affected Products',
            value: strOrUndefined(r.product_description),
          },
          {
            label: 'Affected Locations',
            value: strOrUndefined(r.distribution_pattern),
          },
          {
            label: 'Quantity Recalled',
            value: strOrUndefined(r.product_quantity),
          },
          { label: 'Recall Number', value: strOrUndefined(r.recall_number) },
        ],
      };
    }
    case RECALL_SOURCE.FDA_FOOD: {
      const r = item.record as FdaEnforcementRecord;
      return {
        recallDate,
        status: strOrUndefined(r.status),
        severity: recallSeverityForCard(r.classification),
        subtitle: strOrUndefined(r.recalling_firm),
        title: strOrUndefined(r.product_description),
        fields: [
          {
            label: 'Recall Reason',
            value: strOrUndefined(r.reason_for_recall),
          },
          {
            label: 'Affected Locations',
            value: strOrUndefined(r.distribution_pattern),
          },
          {
            label: 'Affected Products',
            value: strOrUndefined(r.product_description),
          },
          { label: 'Address/Contact Info', value: undefined },
        ],
      };
    }
    case RECALL_SOURCE.USDA: {
      const r = item.record as UsdaRecallRecord;
      const classification = str(r.field_recall_classification);
      const riskLevel = str(r.field_risk_level);
      const contactParts = [
        strOrUndefined(r.field_media_contact),
        strOrUndefined(r.field_company_media_contact),
      ].filter(Boolean) as string[];
      return {
        recallDate,
        status: deriveUsdaRecallStatus(r),
        severity: recallSeverityForCard(
          classification !== ''
            ? classification
            : riskLevel !== ''
            ? riskLevel
            : undefined
        ),
        subtitle: strOrUndefined(r.field_establishment),
        title: strOrUndefined(r.field_title) ?? strOrUndefined(r.field_summary),
        fields: [
          {
            label: 'Recall Reason',
            value: strOrUndefined(r.field_recall_reason),
          },
          {
            label: 'Affected Locations',
            value: strOrUndefined(r.field_states),
          },
          {
            label: 'Affected Products',
            value: strOrUndefined(r.field_product_items),
          },
          {
            label: 'Address/Contact Info',
            value:
              contactParts.length > 0 ? contactParts.join('; ') : undefined,
          },
        ],
      };
    }
    case RECALL_SOURCE.CPSC: {
      const r = item.record as CpscRecallRecord;
      const affectedProducts = Array.isArray(r.Products)
        ? r.Products.map((p) => p?.Name)
            .filter(Boolean)
            .join('; ')
        : undefined;
      const images = Array.isArray(r.Images)
        ? r.Images.map((img) => img?.URL)
            .filter(Boolean)
            .join(', ')
        : undefined;
      const injuries = Array.isArray(r.Injuries)
        ? r.Injuries.map((inj) => inj?.Name)
            .filter(Boolean)
            .join('; ')
        : undefined;
      const productName =
        Array.isArray(r.Products) && r.Products[0]?.Name
          ? strOrUndefined(r.Products[0].Name)
          : undefined;
      return {
        recallDate,
        status: undefined,
        subtitle: productName,
        title: strOrUndefined(r.Title),
        fields: [
          { label: 'Recall Reason', value: strOrUndefined(r.Description) },
          {
            label: 'Address/Contact Info',
            value: strOrUndefined(r.ConsumerContact),
          },
          { label: 'Affected Products', value: affectedProducts },
          { label: 'Injuries', value: injuries },
          { label: 'Images', value: images },
        ],
      };
    }
    default:
      return {
        recallDate,
        status: undefined,
        subtitle: undefined,
        title: undefined,
        fields: [],
      };
  }
}
